// One company's portal (FR-41 … FR-46). Every member sees Overview, Staff
// (read-only for a company_viewer), Lookup, Calls and Billing; only a
// company_admin sees Credentials, Trusted networks and Webhooks; Till payments
// is read-only for others. The server enforces the same rules (P3-4).
import { Alert, Anchor, Group, Loader, Tabs } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { Link, Navigate, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import { errorMessage } from "../../shared/session/errors";
import { SignedIn } from "../../shared/session/guards";
import { FormError, WidePage } from "../../shared/ui/Page";
import { BillingTab } from "./BillingTab";
import { CallsTab } from "./CallsTab";
import { AdminsOnly, CompanyStatusBadge, RoleBadge, useMyCompanies } from "./common";
import { CredentialsTab } from "./CredentialsTab";
import { LookupTab } from "./LookupTab";
import { NetworksTab } from "./NetworksTab";
import { PurposesTab } from "./PurposesTab";
import { OverviewTab } from "./OverviewTab";
import { StaffTab } from "./StaffTab";
import { TillTab } from "./TillTab";
import { DeliveriesView, WebhooksTab } from "./WebhooksTab";

type TabKey =
  "overview" | "staff" | "credentials" | "networks" | "purposes" | "webhooks" | "lookup" | "till" | "calls" | "billing";

export function CompanyPage() {
  return (
    <SignedIn>
      <CompanyView />
    </SignedIn>
  );
}

function CompanyView() {
  const { t } = useTranslation();
  const { companyId = "" } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const mine = useMyCompanies();

  if (mine.isPending) return <Loader size="sm" aria-label={t("common.loading")} my="xl" />;
  if (mine.isError) return <FormError message={errorMessage(mine.error, t("error.generic"))} />;
  const membership = mine.data.items.find((m) => m.company.id === companyId);
  if (!membership) {
    return (
      <WidePage title={t("company.not_found_title")}>
        <Alert color="red" role="alert" data-testid="company-not-found">
          {t("company.not_found")}
        </Alert>
        <Anchor component={Link} to="/business">
          {t("company.back")}
        </Anchor>
      </WidePage>
    );
  }

  const { company, role } = membership;
  const admin = role === "company_admin";
  const base = `/business/${company.id}`;
  const tabs: TabKey[] = admin
    ? ["overview", "staff", "credentials", "networks", "purposes", "webhooks", "lookup", "till", "calls", "billing"]
    : ["overview", "staff", "purposes", "lookup", "till", "calls", "billing"];
  const segment = location.pathname.slice(base.length).split("/")[1] ?? "";
  const current: TabKey = (tabs as string[]).includes(segment) ? (segment as TabKey) : "overview";
  const adminOnly = (el: React.ReactNode) => (admin ? el : <AdminsOnly />);

  return (
    <WidePage title={company.name}>
      <Group gap="sm">
        <CompanyStatusBadge status={company.status} />
        <RoleBadge role={role} />
        <Anchor component={Link} to="/business" size="sm">
          {t("company.back")}
        </Anchor>
      </Group>
      {/* Each tab owns a panel (aria-controls must point at one); only the current one renders the page. */}
      <Tabs
        value={current}
        onChange={(v) => v && navigate(v === "overview" ? base : `${base}/${v}`)}
        aria-label={t("company.sections")}
      >
        <Tabs.List>
          {tabs.map((k) => (
            <Tabs.Tab key={k} value={k}>
              {t(`company.tab_${k}`)}
            </Tabs.Tab>
          ))}
        </Tabs.List>
        {tabs.map((k) => (
          <Tabs.Panel key={k} value={k} pt="lg">
            {k === current && (
              <Routes>
                <Route index element={<OverviewTab company={company} admin={admin} />} />
                <Route path="staff" element={<StaffTab companyId={company.id} admin={admin} />} />
                <Route path="credentials" element={adminOnly(<CredentialsTab company={company} />)} />
                <Route path="networks" element={adminOnly(<NetworksTab companyId={company.id} />)} />
                <Route path="purposes" element={<PurposesTab companyId={company.id} admin={admin} />} />
                <Route path="webhooks" element={adminOnly(<WebhooksTab company={company} />)} />
                <Route path="webhooks/:endpointId" element={adminOnly(<DeliveriesView companyId={company.id} />)} />
                <Route path="lookup" element={<LookupTab companyId={company.id} />} />
                <Route path="till" element={<TillTab companyId={company.id} admin={admin} />} />
                <Route path="calls" element={<CallsTab companyId={company.id} />} />
                <Route path="billing" element={<BillingTab companyId={company.id} />} />
                <Route path="*" element={<Navigate to={base} replace />} />
              </Routes>
            )}
          </Tabs.Panel>
        ))}
      </Tabs>
    </WidePage>
  );
}
